import { useCallback, useEffect, useRef, useState } from 'react';
import { useAuth } from '@ibk/shared-auth';
import { selectIsAuthenticated, useAppSelector } from '@ibk/shared-config';
export function useSessionTimeout(options = {}) {
    const { timeoutMinutes = 15, enabled = true, onSessionExpired, onUserActivity } = options;
    const isAuthenticated = useAppSelector(selectIsAuthenticated);
    const { token } = useAuth();
    const [isSessionExpired, setIsSessionExpired] = useState(false);
    const [timeRemaining, setTimeRemaining] = useState(timeoutMinutes * 60);
    const [isActive, setIsActive] = useState(false);
    const timeoutRef = useRef(null);
    const activityTimeoutRef = useRef(null);
    const lastActivityRef = useRef(Date.now());
    const isInitializedRef = useRef(false);
    const timeoutMs = timeoutMinutes * 60 * 1000;
    const resetTimeout = useCallback((notifyActivity = true) => {
        if (!enabled || !isAuthenticated)
            return;
        if (timeoutRef.current) {
            clearTimeout(timeoutRef.current);
        }
        lastActivityRef.current = Date.now();
        setIsSessionExpired(false);
        setTimeRemaining(timeoutMinutes * 60);
        timeoutRef.current = setTimeout(() => {
            setIsSessionExpired(true);
            onSessionExpired?.();
        }, timeoutMs);
        if (notifyActivity) {
            onUserActivity?.();
        }
    }, [enabled, isAuthenticated, timeoutMinutes, timeoutMs, onSessionExpired, onUserActivity]);
    const triggerSessionExpiration = useCallback(() => {
        setIsSessionExpired(true);
        onSessionExpired?.();
    }, [onSessionExpired]);
    const handleUserActivity = useCallback(() => {
        if (!enabled || !isAuthenticated || isSessionExpired)
            return;
        if (activityTimeoutRef.current) {
            clearTimeout(activityTimeoutRef.current);
        }
        activityTimeoutRef.current = setTimeout(() => {
            resetTimeout();
        }, 500);
    }, [enabled, isAuthenticated, isSessionExpired, resetTimeout]);
    useEffect(() => {
        if (!enabled || !isAuthenticated || isSessionExpired) {
            setTimeRemaining(0);
            return;
        }
        const interval = setInterval(() => {
            const elapsed = Date.now() - lastActivityRef.current;
            const remaining = Math.max(0, timeoutMs - elapsed);
            const remainingSeconds = Math.ceil(remaining / 1000);
            setTimeRemaining(remainingSeconds);
        }, 1000);
        return () => clearInterval(interval);
    }, [enabled, isAuthenticated, isSessionExpired, timeoutMs]);
    useEffect(() => {
        if (enabled && isAuthenticated && token && !isInitializedRef.current) {
            setIsActive(true);
            isInitializedRef.current = true;
            resetTimeout(false);
        }
        else if (!enabled || !isAuthenticated || !token) {
            setIsActive(false);
            setIsSessionExpired(false);
            setTimeRemaining(0);
            isInitializedRef.current = false;
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
                timeoutRef.current = null;
            }
            if (activityTimeoutRef.current) {
                clearTimeout(activityTimeoutRef.current);
                activityTimeoutRef.current = null;
            }
        }
    }, [enabled, isAuthenticated, resetTimeout, token]);
    useEffect(() => {
        if (!enabled || !isAuthenticated || isSessionExpired)
            return;
        const events = ['mousedown', 'keypress', 'click', 'focus', 'touchstart', 'scroll'];
        events.forEach(event => {
            document.addEventListener(event, handleUserActivity, { passive: true });
        });
        return () => {
            events.forEach(event => {
                document.removeEventListener(event, handleUserActivity);
            });
        };
    }, [enabled, isAuthenticated, isSessionExpired, handleUserActivity]);
    useEffect(() => {
        return () => {
            if (timeoutRef.current) {
                clearTimeout(timeoutRef.current);
            }
            if (activityTimeoutRef.current) {
                clearTimeout(activityTimeoutRef.current);
            }
        };
    }, []);
    return {
        isSessionExpired,
        timeRemaining,
        resetTimeout,
        triggerSessionExpiration,
        isActive,
    };
}
